import { useAtom } from "jotai";
import React from "react";
import Icon from "../../components/Icon";
import {
  Menu,
  MenuItem,
  MenuLabel,
  MenuSeparator,
} from "../../components/Menu";
import { BOT_NAME } from "../../consts";
import { colorValue } from "../../css/color";
import {
  audioStreamsAtom,
  currentUserAtom,
  onlinesAtom,
  videoStreamsAtom,
} from "../../jotai/atoms";
import { useSpaceInfo, useUserInfos } from "../../liveblocks/maps";
import { styled } from "../../stitches.config";
import { extractUserId } from "../../utils/session";
import {
  EditColorDialog,
  EditColorDialogTrigger,
  EditNameDialog,
  EditNameDialogTrigger,
} from "./EditUser";

export default function BuddyList() {
  const [currentUser] = useAtom(currentUserAtom);
  const [onlines] = useAtom(onlinesAtom);
  const [info] = useSpaceInfo();

  const [audios] = useAtom(audioStreamsAtom);
  const [videos] = useAtom(videoStreamsAtom);

  const audioUids = Object.keys(audios).map((sid) => extractUserId(sid));
  const videoUids = Object.keys(videos).map((sid) => extractUserId(sid));

  const [userInfos] = useUserInfos();

  const users = userInfos
    ? [...onlines]
        .map((uid) => userInfos[uid])
        .filter((user) => !!user && user.name !== BOT_NAME)
    : [];

  return (
    <>
      <Menu
        icon="People"
        label={`${users.length}`}
        ariaLabel={`Buddy list (${users.length} online)`}
      >
        <MenuLabel>Buddy List</MenuLabel>
        {users.map((user, index) => {
          return (
            <BuddyListMenuItem
              key={index}
              name={user.name}
              color={colorValue(user.color, 80)}
              audio={audioUids.includes(user.id)}
              video={videoUids.includes(user.id)}
              isSelf={currentUser?.id === user.id}
              isOwner={info?.owners?.includes(user.id) ?? false}
            />
          );
        })}
        <MenuSeparator />
        <EditNameDialogTrigger />
        <EditColorDialogTrigger />
      </Menu>
      <EditNameDialog />
      <EditColorDialog />
    </>
  );
}

function BuddyListMenuItem({
  name,
  color,
  audio,
  video,
  isSelf,
  isOwner,
}: {
  name: string;
  color: string;
  audio: boolean;
  video: boolean;
  isSelf: boolean;
  isOwner: boolean;
}) {
  return (
    <MenuItem as={Buddy}>
      <Dot style={{ background: color }} />
      {name}
      {isSelf && ` (me)`}
      <div>
        {video && <Icon icon="CamOn" color="black" />}
        {audio && <Icon icon="MicOn" color="black" />}
      </div>
      {isOwner && <Label>Host</Label>}
    </MenuItem>
  );
}

const Buddy = styled("li", {
  display: "grid",
  gridTemplateColumns: "auto 1fr auto 1fr",
  gridGap: "$1",
  alignItems: "center",
  padding: "$2",
});

const Dot = styled("div", {
  width: "$2",
  height: "$2",
  borderRadius: "$circle",
});

const Label = styled("div", {
  justifySelf: "end",
  color: "$menuTitleGray",
});
